import { saveUsers, showSuccessAlert, spoofRoles } from 'actions';
import {
  applyPermissionsSingleUser,
  fetchUserInvitationState,
  selectUsers,
} from 'actions/userPermissionsPage';
import EnvelopeCollapsableSection from 'components/CollapsableSection/EnvelopeCollapsableSection';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUser } from 'selectors/auth';
import {
  getCanApplySaveSingleUser,
  getExcludedRoles,
  getIncludedRoles,
  getInvitationState,
  getSelectedUsers,
} from 'selectors/userPermissionsPage';
import { useSelector } from 'store';
import { meetsRolesReqs } from 'utils/permissions';
import UserInformationSection from './UserInformationSection';
import UsersChangePermissions from './UsersChangePermissions';

type ComponentProps = {
  footer?: boolean;
  saveInApply?: boolean;
  shouldDeleteUser?: boolean;
  isMainUsersPage: boolean;
  renderExtraOptions?: boolean;
};

const SingleUserSidebar: React.FC<ComponentProps> = ({
  footer = true,
  saveInApply = false,
  shouldDeleteUser = false,
  isMainUsersPage,
  renderExtraOptions = true,
}) => {
  const dispatch = useDispatch();
  const selectedUsers = useSelector(getSelectedUsers);
  const canApplySingleUser = useSelector((state) => getCanApplySaveSingleUser(state, 'false'));
  const invitationState = useSelector(getInvitationState);

  const user = useSelector(getUser);

  const [sendInvitation, setSendInvitation] = useState(true);

  const includedRoles = useSelector(getIncludedRoles);
  const excludedRoles = useSelector(getExcludedRoles);

  const [canSendInvitation, setCanSendInvitation] = useState(false);

  const renderSettings = (): boolean => meetsRolesReqs(user.roles, ['super-admin', 'system-admin']);

  useEffect(() => {
    const reviewerRoles = [
      'super-reviewer',
      'legal-user',
      'reviewer',
      'super-user',
      'system-admin',
    ];
    const canSend = selectedUsers[0]?.roles.some((role) => reviewerRoles.includes(role));
    setCanSendInvitation(canSend);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers.length, includedRoles, excludedRoles]);

  useEffect(() => {
    if (renderSettings()) {
      dispatch(fetchUserInvitationState({ userId: selectedUsers[0].uuid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedUsers]);

  const handleSendInvitation = (event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>): void => {
    event.stopPropagation();

    dispatch(saveUsers({ users: selectedUsers, fromPermissionsPage: true, sendInvitation: true }));
  };

  const handleCopyClick = async (
    event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>
  ): Promise<void> => {
    event.stopPropagation();
    const ids = selectedUsers.reduce((acc, value, idx) => {
      if (idx === 0) {
        return value.uuid;
      }
      return `${acc}, ${value.uuid}`;
    }, '');
    await navigator.clipboard.writeText(ids);
    dispatch(showSuccessAlert('User UUID(s) copied to your clipboard'));
  };

  const handleSpoofClick = (event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>): void => {
    event.stopPropagation();
    dispatch(
      spoofRoles({
        userUuid: selectedUsers[0].uuid,
        userEmail: selectedUsers[0].email || undefined,
      })
    );
    dispatch(showSuccessAlert('View Changed'));
  };

  const handleApply = (): void => {
    dispatch(applyPermissionsSingleUser());
  };

  const handleClose = (): void => {
    dispatch(selectUsers([]));
  };

  return (
    <div className="relative h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto no-scrollbar">
        <div className="h-full">
          <EnvelopeCollapsableSection headerTitle="Selected User" closeAction={handleClose}>
            <>
              {renderExtraOptions && (
                <div className="px-3 py-2 flex flex-row gap-2 bg-litlingo-gray-1 bg-opacity-50">
                  <button
                    onClick={(e): void => handleSpoofClick(e)}
                    type="submit"
                    className="button button--secondary h-8"
                    data-testid="save-button"
                    disabled={selectedUsers.length > 1}
                  >
                    <span className="font-bold whitespace-no-wrap">View As</span>
                  </button>
                  {canSendInvitation && isMainUsersPage && (
                    <button
                      onClick={(e): void => handleSendInvitation(e)}
                      type="submit"
                      className="button button--secondary h-8"
                      data-testid="save-button"
                    >
                      <span className="font-bold whitespace-no-wrap">Send Invitation</span>
                    </button>
                  )}
                  <button
                    onClick={(e): void => {
                      handleCopyClick(e);
                    }}
                    type="submit"
                    className="flex button button--secondary h-8"
                    data-testid="save-button"
                  >
                    <span className="font-bold whitespace-no-wrap">Copy ID</span>
                  </button>
                </div>
              )}
              <div className="pt-4">
                <UserInformationSection
                  user={selectedUsers[0]}
                  shouldDeleteUser={shouldDeleteUser}
                  invitationState={invitationState}
                />
              </div>
              <UsersChangePermissions
                saveInApply={saveInApply}
                sendInvitation={sendInvitation}
                setSendInvitation={setSendInvitation}
                canSendInvitation={canSendInvitation && isMainUsersPage}
              />
            </>
          </EnvelopeCollapsableSection>
        </div>
      </div>

      {footer && (
        <div
          className="flex flex-col gap-1 bg-white border border-litlingo-gray-1 p-4"
          style={{ filter: 'drop-shadow(3px 1px 8px rgba(0, 0, 0, 0.25))' }}
        >
          <div className="flex flex-row items-center justify-between gap-6 h-8">
            <button
              type="button"
              data-testid="next-envelope-button"
              className="button button--secondary w-27 py-1.5 h-full"
              onClick={handleClose}
            >
              <div className="flex w-full justify-center items-center font-bold">Cancel</div>
            </button>
            <button
              type="button"
              id="next-envelope-button"
              data-testid="next-envelope-button"
              className="button  button--primary min-w-27 h-full py-1.5 focus:outline-none"
              onClick={handleApply}
              disabled={!canApplySingleUser}
            >
              <div className="flex w-full justify-center items-center text-base font-bold leading-5">
                Apply
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default SingleUserSidebar;
